import React, { useState, useEffect, useCallback, useMemo } from "react";
import classNames from "classnames";
import { DragDropContext } from "react-beautiful-dnd";
import { nanoid } from "nanoid";
import Button from "shared/Button";
import AddNewModal from "ManagementTeam/Shared/AddNewModal";
import MoveModal from "ManagementTeam/Shared/MoveModal";

import { ReactComponent as DeleteIcon } from "../../../assets/Delete_Icon.svg";
import ReactModal from "react-modal";
import styles from "./AgendaBody.module.css";
import { useDocuments } from "ManagementTeam/DocumentsPage/useDocuments";
import Auth from "shared/@auth";
import { Dashboard } from "ManagementTeam/DashboardPage/useDashboard";
import AgendaDragDropContainer from "ManagementTeam/AgendaPage/AgendaDragDropContainer";
import { useAgenda } from "ManagementTeam/AgendaPage/useAgenda";

export function AgendaBody({
  agendas,
  itemOwners,
  status,
  setIsModalOpen,
  readOnly = false,
  users = [],
  meetingDateOptions = [],
  meetingId, //for adding new agenda
  meetingType, //for adding new agenda
  showAddNewModal,
  setShowAddNewModal,
  isActionItem = false,
  onAfterEditSuccess,
  attendanceItem = null,
  type,
  isHideInfo,
}) {
  const id = nanoid();
  const {
    agendaPost,
    editAgendaPut,
    deleteAgenda,
    completeAgendaPut,
    moveAgendaPut,
    duplicateAgendaPost,
    actionItemEditPut,
    agendaItemOrderPut,
    agendaItemPublishPut,
    fetchPdf,
  } = useAgenda();
  const { refetchDashboard } = Dashboard.useContainer();
  const { account } = Auth.useContainer();
  const { downloadFile, unassignedRefetch } = useDocuments();

  const [showEditModal, setShowEditModal] = useState(false);
  const [editingItem, setEditingItem] = useState({});
  const [deleteModelOpen, setDeleteModelOpen] = useState(false);
  const [deleteAgendaId, setDeleteAgendaId] = useState("");
  const [showMoveModal, setShowMoveModal] = useState(false);
  const [movingItem, setMovingItem] = useState({});

  const [editingAgendaId, setEditingAgendaId] = useState(null);
  const [currentAgendaItems, setCurrentAgendaItems] = useState(agendas);

  const [pdfMeetings, setPdfMeetings] = useState([]);
  const [pdfAgendas, setPdfAgendas] = useState([]);

  useEffect(() => {
    if (agendas) {
      setCurrentAgendaItems(agendas);
    }
  }, [agendas]);

  //Logic for publishing the agenda PDF goes here
  const handlePublishAgenda = (data) => {
    agendaItemPublishPut.mutate({
      agendaId: data._id,
      published: true,
    });
  };

  // POST New Agenda
  const handleSaveNew = (data) => {
    const agendaObject = {
      agendaId: null,
      meetingId: meetingId,
      documentId: data.documentId,
      isDocumentDownloadable: data.isDocumentDownloadable,
      filesToUpload: data.isUploadDocument ? data.documents : [],
      meetingType: meetingType,
      owner: {
        _id: data.itemOwner.id,
        name: data.itemOwner.option,
        photoUrl: data.itemOwner.photoUrl,
        accessLevel: data.itemOwner.accessLevel,
      },
      title: data.title,
      description: data.description,
      notes: data.notes.map((note) => ({
        note: note.content,
        postedBy: {
          name: note.author,
          _id: note.postedBy
            ? note.postedBy._id
            : users.find((user) => user.profile.email === note.username)._id,
        },
        location: note.location,
        submittedOn: note.date,
      })),
      decision: data.addTo.includes("Decisions (Voting)")
        ? {
            // requiresQuorum: data.votingRequirements.includes("Requires Quorum"),
            isBlockA: data.votingRequirements.includes('"A Block" Vote'),
            isBlockB: data.votingRequirements.includes('"B Block" Vote'),
            isMultipleChoice:
              data.votingRequirements.includes("Multiple Choice"),
            maximumChoices: data.allowMultiple ? data.maximumChoices : 1,
            choices: data.selections,
            votingStatus: "Upcoming",
            status: "off",
            displayResults: false,
          }
        : null,
      isDraft: false,
      ifNeeded: data.ifNeeded,
      document: {
        files: data.documents,
      },
    };
    agendaPost.mutateAsync(agendaObject).then(() => {
      refetchDashboard();
      setShowAddNewModal(false);
      setIsModalOpen(false);
    });
  };

  const handleSaveNewDraft = (data) => {
    const agendaObject = {
      agendaId: null,
      meetingId: meetingId,
      documentId: data.documentId,
      isDocumentDownloadable: data.isDocumentDownloadable,
      filesToUpload: data.isUploadDocument ? data.documents : [],
      meetingType: meetingType,
      owner: {
        _id: data.itemOwner.id,
        name: data.itemOwner.option,
        photoUrl: data.itemOwner.photoUrl,
        accessLevel: data.itemOwner.accessLevel,
      },
      title: data.title,
      description: data.description,
      notes: data.notes.map((note) => ({
        note: note.content,
        postedBy: {
          name: note.author,
          _id: note.postedBy
            ? note.postedBy._id
            : users.find((user) => user.profile.email === note.username)._id,
        },
        location: note.location,
      })),
      decision: data.addTo.includes("Decisions (Voting)")
        ? {
            isBlockA: data.votingRequirements.includes('"A Block" Vote'),
            isBlockB: data.votingRequirements.includes('"B Block" Vote'),
            isMultipleChoice:
              data.votingRequirements.includes("Multiple Choice"),
            maximumChoices: data.allowMultiple ? data.maximumChoices : 1,
            choices: data.selections,
            votingStatus: "Upcoming",
            status: "off",
            displayResults: false,
          }
        : null,
      isDraft: true,
      ifNeeded: data.ifNeeded,
    };

    agendaPost.mutateAsync(agendaObject).then(() => {
      refetchDashboard();
      setShowAddNewModal(false);
      setShowEditModal(false);
      setIsModalOpen(false);
    });
  };

  const handleSaveEdit = (data) => {
    const agendaObject = {
      meetingId: data.meetingId,
      agendaId: editingAgendaId,
      documentId: data.documentId,
      isDocumentDownloadable: data.isDocumentDownloadable,
      filesToUpload: data.isUploadDocument ? data.documents : [],
      meetingType: meetingType,
      owner: {
        _id: data.itemOwner.id,
        name: data.itemOwner.option,
        photoUrl: data.itemOwner.photoUrl,
        accessLevel: data.itemOwner.accessLevel,
      },
      title: data.title,
      description: data.description,
      notes:
        (data.notes &&
          data.notes.map((note) => {
            let postedBy = {
              name: note.author,
            };

            if (note.postedBy) {
              postedBy._id = note.postedBy._id;
            } else {
              let user =
                (users &&
                  users.find((user) => user.profile.email === note.username)) ||
                null;
              if (user) {
                postedBy._id = user._id;
              }
            }
            return {
              note: note.content,
              postedBy: postedBy,
              submittedOn: note.date,
              location: note.location,
            };
          })) ||
        [],
      decision: data.addTo.includes("Decisions (Voting)")
        ? {
            isBlockA: data.votingRequirements.includes('"A Block" Vote'),
            isBlockB: data.votingRequirements.includes('"B Block" Vote'),
            isMultipleChoice:
              data.votingRequirements.includes("Multiple Choice"),
            maximumChoices: data.allowMultiple ? data.maximumChoices : 1,
            choices: data.selections,
            votingStatus: "Upcoming",
            status: "off",
            displayResults: false,
          }
        : null,
      isDraft: false,
      ifNeeded: data.ifNeeded,
    };
    editAgendaPut.mutate(agendaObject, {
      onSuccess: () => {
        onAfterEditSuccess && onAfterEditSuccess();
      },
    });
    agendaObject.isNew = false;
    setShowEditModal(false);
    setIsModalOpen(false);
    setTimeout(() => {
      refetchDashboard();
    }, 1000);
  };

  const handleEdit = (data) => {
    if (data.files && data.files.length > 0) {
      Promise.all(
        data.files.map(async (file) => {
          return {
            type: file.type,
            src: file.src,
            data: await fetchPdf(
              file.src.match(/agendas\/.*?$/g)[0].replace("agendas/", "")
            ).then(async (res) => {
              const blob = await res.blob();
              const url = window.URL.createObjectURL(blob);
              const response = await fetch(url);
              const res_1 = response.text();
              return res_1;
            }),
          };
        })
      ).then((result) => {
        const notes = data.notes.map((note) => {
          return {
            content: note.note,
            author: note.postedBy.name,
            date: note.submittedOn,
            postedBy: note.postedBy,
            location: note.location,
          };
        });
        setEditingAgendaId(data._id);
        const choices = data.decision.choices.map(
          (choice) => choice.description
        );
        const itemInfo = {
          id: data._id,
          meetingId: data.meeting,
          isDraft: data.isDraft,
          isActionItem: data.isActionItem,
          itemOwner: data.owner,
          title: data.title,
          description: data.description,
          notes: [...notes],
          addTo: ["Agenda", choices.length > 0 ? "Decisions (Voting)" : null],
          votingRequirements: [],
          allowMultiple: data.decision.maximumChoices > 1,
          maximumChoices: data.decision.maximumChoices,
          selections: [...choices],
          documents: result,
        };
        if (data.decision.isBlockA) {
          itemInfo.votingRequirements.push('"A Block" Vote');
        }
        if (data.decision.isBlockB) {
          itemInfo.votingRequirements.push('"B Block" Vote');
        }
        if (data.decision.isMultipleChoice) {
          itemInfo.votingRequirements.push("Multiple Choice");
        }
        setEditingItem(itemInfo);
        setShowEditModal(true);
        setIsModalOpen(true);
      });
    } else {
      const notes = data.notes?.map((note) => {
        return {
          content: note.note,
          author: note.postedBy.name,
          date: note.submittedOn,
          postedBy: note.postedBy,
          location: note.location,
        };
      });
      setEditingAgendaId(data._id);
      const choices = data.decision.choices.map((choice) => choice.description);
      const itemInfo = {
        id: data._id,
        meetingId: data.meeting,
        isActionItem: data.isActionItem,
        isDraft: data.isDraft,
        itemOwner: data.owner,
        title: data.title,
        description: data.description,
        notes: notes ? [...notes] : [],
        addTo: ["Agenda", choices.length > 0 ? "Decisions (Voting)" : null],
        votingRequirements: [],
        allowMultiple: data.decision.maximumChoices > 1,
        maximumChoices: data.decision.maximumChoices,
        selections: [...choices],
        document: data.document,
        documents: [],
      };
      if (data.decision.isBlockA) {
        itemInfo.votingRequirements.push('"A Block" Vote');
      }
      if (data.decision.isBlockB) {
        itemInfo.votingRequirements.push('"B Block" Vote');
      }
      if (data.decision.isMultipleChoice) {
        itemInfo.votingRequirements.push("Multiple Choice");
      }
      setEditingItem(itemInfo);
      setShowEditModal(true);
      setIsModalOpen(true);
    }
  };

  const handleDeleteAgenda = (selectedId) => {
    deleteAgenda.mutate(
      {
        agendaId: selectedId,
      },
      {
        onSuccess: () => {
          refetchDashboard();
          onAfterEditSuccess && onAfterEditSuccess();
        },
      }
    );
  };

  const handleOpenMove = (data) => {
    setMovingItem(data);
    setShowMoveModal(true);
    setIsModalOpen(true);
  };

  const handleMove = (destinatedMeeting) => {
    moveAgendaPut.mutate({
      agendaId: movingItem._id,
      addToMeetingId: destinatedMeeting.id,
    });
    setShowMoveModal(false);
    setIsModalOpen(false);
    refetchDashboard();
  };

  const handleDuplicate = (destinatedMeeting) => {
    duplicateAgendaPost.mutate({
      agendaId: movingItem._id,
      addToMeetingId: destinatedMeeting.id,
    });
    setShowMoveModal(false);
    setIsModalOpen(false);
    refetchDashboard();
  };

  const handleCompleteAgenda = (data) => {
    completeAgendaPut.mutate({
      agendaId: data._id,
    });
  };

  //Handles the rearranging of dragged agenda items after drag
  const handleDragEnd = useCallback(
    async (result) => {
      const { destination, source } = result;
      if (!destination) {
        return;
      }
      if (
        destination.droppableId === source.droppableId &&
        destination.index === source.index
      ) {
        return;
      }

      let items = [...currentAgendaItems];
      const [reorderedItem] = items.splice(result.source.index, 1);
      items.splice(result.destination.index, 0, reorderedItem);

      // Optimistic update for smooth UX
      setCurrentAgendaItems(items);

      // Save to backend and refetch to ensure consistency
      agendaItemOrderPut
        .mutateAsync({
          meetingId: reorderedItem.meeting,
          agendaId: reorderedItem.id,
          order: result.destination.index,
        })
        .then(() => {
          refetchDashboard();
        })
        .catch((error) => {
          // On error, refetch to restore correct order from backend
          console.error("Failed to reorder agenda item:", error);
          refetchDashboard();
        });
    },
    [agendaItemOrderPut, currentAgendaItems, refetchDashboard]
  );

  const handleActionItem = (data) => {
    actionItemEditPut.mutate(
      {
        agendaId: data._id,
      },
      {
        onSuccess: () => {
          refetchDashboard();
        },
      }
    );
  };

  const handleDownloadPdf = (e) => {
    e.map(async (file) => {
      await downloadFile(file.downloadPath);
    });
  };
  const deleteMessage = useMemo(() => {
    if (type === "agenda") {
      return "Are you sure you want to delete this agenda item and any items associated with it?";
    } else if (type === "action items") {
      return "Are you sure you want to remove this action item from the agenda?";
    }
    return "";
  }, [type]);
  const renderAddModal = useCallback(() => {
    return (
      <>
        {account && showAddNewModal && (
          <AddNewModal
            key={id}
            isOpen={showAddNewModal}
            onClose={() => {
              setShowAddNewModal(false);
              setIsModalOpen(false);
              refetchDashboard();
            }}
            meetingDates={meetingDateOptions}
            onSave={handleSaveNew}
            onSaveDraft={handleSaveNewDraft}
            currentUser={{ name: account.name, username: account.username }}
            itemOwners={itemOwners}
          />
        )}
        {account && editingItem && showEditModal && (
          <AddNewModal
            isOpen={showEditModal}
            onClose={() => {
              setShowEditModal(false);
              setIsModalOpen(false);
              refetchDashboard();
            }}
            meetingDates={meetingDateOptions}
            isActionItem={isActionItem}
            onSave={handleSaveEdit}
            onSaveDraft={handleSaveNewDraft}
            currentUser={{ name: account.name, username: account.username }}
            itemInfo={editingItem}
            itemOwners={itemOwners}
            users={users}
          />
        )}
      </>
    );
  }, [showEditModal, showAddNewModal, editingItem]);
  return status === "loading" ? (
    <div className={styles.noAgendas}>Loading</div>
  ) : (
    <div className={classNames(styles.container)}>
      <ReactModal
        isOpen={deleteModelOpen}
        className={styles.deleteModal}
        overlayClassName={styles.overlay}
      >
        <div className={styles.deleteModalHeader}>
          <DeleteIcon />
          <p>{deleteMessage}</p>
        </div>
        <div className={styles.deleteModalButtons}>
          <Button
            className={styles.cancelButton}
            children="Cancel"
            onClick={() => {
              setDeleteModelOpen(false);
              refetchDashboard();
            }}
          />
          <Button
            className={styles.deleteButton}
            children="Delete"
            onClick={() => {
              handleDeleteAgenda(deleteAgendaId);
              setDeleteModelOpen(false);
              refetchDashboard();
            }}
            purpose="delete"
          />
        </div>
      </ReactModal>
      <div>
        {renderAddModal()}
        {showMoveModal && (
          <MoveModal
            isOpen={showMoveModal}
            onClose={() => {
              setShowMoveModal(false);
              setIsModalOpen(false);
              refetchDashboard();
            }}
            onMove={handleMove}
            onDuplicate={handleDuplicate}
          />
        )}
      </div>
      <span className={styles.divider} />
      {currentAgendaItems !== null && currentAgendaItems.length > 0 ? (
        <>
          <DragDropContext onDragEnd={handleDragEnd}>
            <div style={{ padding: "0em 6em 1em 0.75em" }}>
              <AgendaDragDropContainer
                readOnly={readOnly}
                dropdownData={currentAgendaItems}
                onClickPdf={(e) => handleDownloadPdf(e)}
                onActionItem={handleActionItem}
                onDelete={(data) => {
                  setDeleteAgendaId(data.id);
                  setDeleteModelOpen(true);
                }}
                onMove={handleOpenMove}
                onEdit={handleEdit}
                onComplete={handleCompleteAgenda}
                onPublish={handlePublishAgenda}
                isArchived={false}
                meetingDates={/* meetingDates */ []}
                isActionItem={isActionItem}
                attendanceItem={attendanceItem}
                isHideInfo={isHideInfo}
              />
            </div>
          </DragDropContext>
        </>
      ) : (
        <div className={styles.noAgendas}>No Upcoming Agendas</div>
      )}
    </div>
  );
}
